import { useState, useCallback } from "react";
import api from "../../services/api";
import {
  LineChart, Line,
  BarChart, Bar,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import ProtectedLink from "../../components/ProtectedLink";

const LINE_COLORS  = ["#3b82f6", "#16a34a", "#f59e0b", "#ef4444"];
const HYPO_COLORS  = { central:"#3b82f6", optimiste:"#16a34a", pessimiste:"#f59e0b", crise:"#ef4444", libre:"#8b5cf6" };
const MONTH_ABBR   = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];

function fmt(n)  { return (n||0).toLocaleString("fr-FR",{maximumFractionDigits:0})+" €"; }
function fmtK(n) { return (Math.abs(n||0)/1000).toFixed(0)+"k€"; }
function delta(a,b,ref) {
  if (!ref || ref === 0) return "—";
  const d = ((a-b)/Math.abs(b)*100).toFixed(1);
  return (d>0?"+":"")+d+"%";
}

// Seasonal distribution (roughly business-like, Q4 heavier)
const SEASON = [0.07,0.07,0.08,0.08,0.09,0.09,0.07,0.07,0.08,0.09,0.1,0.11];
function buildMonthly(annual) {
  return MONTH_ABBR.map((_, i) => Math.round((annual||0) * SEASON[i]));
}

// Badge for the "winner" scenario
function WinnerBadge({ color }) {
  return <span style={{fontSize:10,background:color+"22",color,borderRadius:10,padding:"1px 7px",fontWeight:700}}>★</span>;
}

const card = {
  background:"var(--card-bg,#fff)",
  border:"1px solid var(--border-color,#e2e8f0)",
  borderRadius:12, padding:20, marginBottom:16,
};

export default function CompareTab({ scenarios, currentCompanyId }) {
  const [selected,  setSelected]  = useState([]);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [chartMode, setChartMode] = useState("ca");   // ca | charges | resultat
  const [msg,       setMsg]       = useState("");

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x=>x!==id) : prev.length<4 ? [...prev,id] : prev
    );
    setData(null);
  };

  const compare = useCallback(async () => {
    if (selected.length < 2) return setMsg("Sélectionnez au moins 2 scénarios");
    setLoading(true); setMsg("");
    try {
      const r = await api.budget.compareMulti(selected);
      setData(r);
    } catch(e) { setMsg("Erreur : "+e.message); }
    finally { setLoading(false); }
  }, [selected]);

  // ── Chart data ────────────────────────────────────────────────
  // Build monthly multi-curve from summaries (synthetic seasonal spread)
  const chartData = data ? MONTH_ABBR.map((month, mi) => {
    const pt = { month };
    (data.summaries || []).forEach((s, si) => {
      const annual = chartMode === "ca"       ? (s.ca        || 0)
                   : chartMode === "charges"  ? (s.charges   || 0)
                   : (s.resultat  || 0);
      pt[`s${si}`] = Math.round(annual * SEASON[mi]);
    });
    return pt;
  }) : [];

  // Annual bar chart data (one bar per scenario per metric)
  const barData = data ? [
    { metric:"CA",       ...Object.fromEntries((data.summaries||[]).map((s,i)=>[`s${i}`,s.ca||0])) },
    { metric:"Charges",  ...Object.fromEntries((data.summaries||[]).map((s,i)=>[`s${i}`,Math.abs(s.charges||0)])) },
    { metric:"Résultat", ...Object.fromEntries((data.summaries||[]).map((s,i)=>[`s${i}`,s.resultat||0])) },
  ] : [];

  // Radar data: normalize each metric to %
  const radarData = data ? (()=>{
    const sums = data.summaries || [];
    const maxCA    = Math.max(...sums.map(s=>Math.abs(s.ca||0)), 1);
    const maxResul = Math.max(...sums.map(s=>Math.abs(s.resultat||0)), 1);
    const maxMarge = Math.max(...sums.map(s=>Math.abs(s.marge_nette||0)), 1);
    const maxEbe   = Math.max(...sums.map(s=>Math.abs(s.ebe||0)), 1);
    return ["CA","EBE","Résultat","Marge nette"].map((label,ri) => {
      const pt = { label };
      sums.forEach((s,si)=>{
        const raw = ri===0 ? s.ca : ri===1 ? (s.ebe||0) : ri===2 ? s.resultat : (s.marge_nette||0);
        const mx  = [maxCA,maxEbe,maxResul,maxMarge][ri];
        pt[`s${si}`] = mx > 0 ? Math.round((raw/mx)*100) : 0;
      });
      return pt;
    });
  })() : [];

  // Metric winner per row
  function winnerIdx(values, higher=true) {
    if (!values || values.length === 0) return -1;
    return higher
      ? values.reduce((best,v,i)=>v>values[best]?i:best, 0)
      : values.reduce((best,v,i)=>v<values[best]?i:best, 0);
  }

  const st = { background:"var(--input-bg,#fff)", border:"1px solid var(--border-color,#e2e8f0)", borderRadius:8, padding:"6px 10px", fontSize:12 };

  return (
    <div style={{display:"flex",gap:18,flexWrap:"wrap",alignItems:"flex-start"}}>

      {/* ── Selector panel ─────────────────────────────────────── */}
      <div style={{...card,minWidth:260,width:260,marginBottom:0}}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:6}}>Sélection scénarios</div>
        <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:12}}>2 à 4 scénarios maximum</div>

        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {(scenarios||[]).filter(s=>s.status!=="archivé").map(sc=>{
            const isSel = selected.includes(sc.id);
            const idx   = selected.indexOf(sc.id);
            const hypo  = sc.hypothesis || "central";
            return (
              <div key={sc.id} onClick={()=>toggle(sc.id)} style={{
                display:"flex",alignItems:"center",gap:8,padding:"9px 10px",
                border:`2px solid ${isSel ? LINE_COLORS[idx] : "#e5e7eb"}`,
                borderRadius:8,cursor:selected.length>=4&&!isSel?"not-allowed":"pointer",
                background:isSel?`${LINE_COLORS[idx]}10`:"transparent",
                opacity:selected.length>=4&&!isSel?0.4:1,transition:"all .15s",
              }}>
                <div style={{width:10,height:10,borderRadius:"50%",background:isSel?LINE_COLORS[idx]:"#e5e7eb",flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:isSel?700:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sc.name}</div>
                  <div style={{fontSize:10,color:"var(--text-muted)"}}>
                    {sc.fiscal_year} · <span style={{color:HYPO_COLORS[hypo]}}>{hypo}</span>
                  </div>
                </div>
                {isSel && <span style={{fontSize:11,fontWeight:700,color:LINE_COLORS[idx],flexShrink:0}}>#{idx+1}</span>}
              </div>
            );
          })}
        </div>

        {scenarios.filter(s=>s.status!=="archivé").length===0 && (
          <div style={{textAlign:"center",padding:"20px 0",color:"var(--text-muted)",fontSize:12}}>
            Aucun scénario actif. Créez des scénarios dans l'onglet Scénarios.
          </div>
        )}

        <button onClick={compare} disabled={selected.length<2||loading} style={{
          marginTop:16,width:"100%",padding:"9px 0",background:selected.length<2?"#e5e7eb":"#3b82f6",
          color:selected.length<2?"#9ca3af":"#fff",border:"none",borderRadius:8,
          fontSize:13,fontWeight:600,cursor:selected.length<2?"not-allowed":"pointer",transition:"all .15s",
        }}>
          {loading ? "Comparaison…" : `⚖️ Comparer ${selected.length} scénario${selected.length>1?"s":""}`}
        </button>

        {msg && <div style={{fontSize:12,color:"#dc2626",marginTop:8,textAlign:"center"}}>{msg}</div>}

        {data && (
          <ProtectedLink url={api.budget.exportScenariosUrl(selected)} style={{
            display:"block",marginTop:10,textAlign:"center",fontSize:12,
            color:"#16a34a",textDecoration:"none",fontWeight:600,
          }}>⬇ Exporter comparaison Excel</ProtectedLink>
        )}
      </div>

      {/* ── Results ────────────────────────────────────────────── */}
      {data ? (
        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:16}}>

          {/* KPI cards */}
          <div style={{display:"grid",gridTemplateColumns:`repeat(${data.summaries.length},1fr)`,gap:12}}>
            {data.summaries.map((s,i)=>{
              const bestCA    = winnerIdx(data.summaries.map(x=>x.ca||0));
              const bestRN    = winnerIdx(data.summaries.map(x=>x.resultat||0));
              const bestMarge = winnerIdx(data.summaries.map(x=>x.marge_nette||0));
              return (
                <div key={s.id} style={{...card,marginBottom:0,border:`2px solid ${LINE_COLORS[i]}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:LINE_COLORS[i],marginBottom:10,display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{width:10,height:10,borderRadius:"50%",background:LINE_COLORS[i],display:"inline-block"}}/>
                    #{i+1} {s.name}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {[
                      {label:"CA total",    val:fmt(s.ca),         raw:s.ca,        winner:bestCA===i,    higher:true},
                      {label:"Charges",     val:fmt(s.charges),    raw:s.charges,   winner:false,         higher:false},
                      {label:"EBE",         val:fmt(s.ebe||0),     raw:s.ebe||0,    winner:false,         higher:true},
                      {label:"Résultat net",val:fmt(s.resultat),   raw:s.resultat,  winner:bestRN===i,    higher:true},
                      {label:"Marge nette", val:(s.marge_nette||0).toFixed(1)+"%",  winner:bestMarge===i, higher:true},
                    ].map(({label,val,raw,winner,higher})=>(
                      <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
                        <span style={{color:"var(--text-muted)",fontSize:11}}>{label}</span>
                        <span style={{fontWeight:winner?700:500,color:winner?LINE_COLORS[i]:"var(--text,#1f2937)"}}>
                          {val}{winner&&<WinnerBadge color={LINE_COLORS[i]}/>}
                        </span>
                      </div>
                    ))}
                    {i>0 && data.summaries[0] && (
                      <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid #f3f4f6",fontSize:11,color:"var(--text-muted)"}}>
                        vs réf : CA {delta(s.ca,data.summaries[0].ca,data.summaries[0].ca)} · RN {delta(s.resultat,data.summaries[0].resultat,data.summaries[0].resultat)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Multi-curve monthly chart */}
          <div style={card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:14,fontWeight:700}}>Évolution mensuelle comparative</div>
              <div style={{display:"flex",gap:6}}>
                {[["ca","CA"],["charges","Charges"],["resultat","Résultat"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setChartMode(v)} style={{
                    padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${chartMode===v?"#3b82f6":"#e5e7eb"}`,
                    background:chartMode===v?"#3b82f6":"transparent",
                    color:chartMode===v?"#fff":"var(--text-muted)",
                  }}>{l}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{top:4,right:10,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="month" tick={{fontSize:10}}/>
                <YAxis tick={{fontSize:10}} tickFormatter={fmtK}/>
                <Tooltip formatter={(v,n)=>[fmt(v),n]}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                {data.summaries.map((s,i)=>(
                  <Line key={s.id} type="monotone" dataKey={`s${i}`} name={s.name}
                    stroke={LINE_COLORS[i]} strokeWidth={2} dot={false}
                    strokeDasharray={i===0?undefined:i===1?"6 3":i===2?"3 3":"8 2 2 2"}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Side-by-side annual bar chart */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Comparaison annuelle par indicateur</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} margin={{top:4,right:10,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="metric" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:10}} tickFormatter={fmtK}/>
                <Tooltip formatter={(v,n)=>[fmt(v),n]}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                {data.summaries.map((s,i)=>(
                  <Bar key={s.id} dataKey={`s${i}`} name={s.name} fill={LINE_COLORS[i]} radius={[3,3,0,0]}/>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Radar chart */}
          {radarData.length>0 && (
            <div style={card}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Profil multi-critères (normalisé)</div>
              <ResponsiveContainer width="100%" height={240}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#f3f4f6"/>
                  <PolarAngleAxis dataKey="label" tick={{fontSize:11}}/>
                  {data.summaries.map((s,i)=>(
                    <Radar key={s.id} name={s.name} dataKey={`s${i}`}
                      stroke={LINE_COLORS[i]} fill={LINE_COLORS[i]} fillOpacity={0.1}/>
                  ))}
                  <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                  <Tooltip formatter={v=>`${v}%`}/>
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Differential table */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Tableau différentiel par poste budgétaire</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb",fontSize:11,whiteSpace:"nowrap"}}>Compte</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb",fontSize:11}}>Désignation</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb",fontSize:11,whiteSpace:"nowrap"}}>Catégorie</th>
                    {data.scenarios.map((sc,i)=>(
                      <th key={sc.id} style={{padding:"8px 10px",textAlign:"right",fontWeight:600,color:LINE_COLORS[i],borderBottom:"1px solid #e5e7eb",fontSize:11,whiteSpace:"nowrap"}}>
                        #{i+1} {sc.name}
                      </th>
                    ))}
                    {data.scenarios.length>1 && (
                      <th style={{padding:"8px 10px",textAlign:"right",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb",fontSize:11,whiteSpace:"nowrap"}}>Écart #2−#1</th>
                    )}
                    {data.scenarios.length>1 && (
                      <th style={{padding:"8px 10px",textAlign:"right",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb",fontSize:11,whiteSpace:"nowrap"}}>Écart %</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(data.rows||[]).map(r=>{
                    const diff = (r.values[1]||0)-(r.values[0]||0);
                    const ref  = r.values[0]||0;
                    const pctD = ref!==0 ? ((diff/Math.abs(ref))*100).toFixed(1) : "—";
                    const isPos = diff >= 0;
                    const wIdx  = winnerIdx(r.values, r.category!=="charge");
                    return (
                      <tr key={r.account_id} style={{borderBottom:"1px solid #f3f4f6"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td style={{padding:"6px 10px",color:"#6b7280",fontSize:11,fontFamily:"monospace"}}>{r.account_id}</td>
                        <td style={{padding:"6px 10px"}}>{r.account_name}</td>
                        <td style={{padding:"6px 10px"}}>
                          <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,
                            background:r.category==="produit"?"#dcfce7":r.category==="charge"?"#fef2f2":"#f3f4f6",
                            color:r.category==="produit"?"#16a34a":r.category==="charge"?"#dc2626":"#374151"}}>
                            {r.category}
                          </span>
                        </td>
                        {(r.values||[]).map((v,i)=>(
                          <td key={i} style={{padding:"6px 10px",textAlign:"right",
                            fontWeight:i===wIdx||i===0?700:400,
                            color:i===wIdx?LINE_COLORS[i]:"inherit"}}>
                            {fmt(v)}
                          </td>
                        ))}
                        {data.scenarios.length>1 && (
                          <td style={{padding:"6px 10px",textAlign:"right",fontWeight:600,
                            color:isPos?(r.category==="charge"?"#dc2626":"#16a34a"):(r.category==="charge"?"#16a34a":"#dc2626")}}>
                            {diff>=0?"+":""}{fmt(diff)}
                          </td>
                        )}
                        {data.scenarios.length>1 && (
                          <td style={{padding:"6px 10px",textAlign:"right",fontWeight:600,fontSize:11,
                            color:isPos?(r.category==="charge"?"#dc2626":"#16a34a"):(r.category==="charge"?"#16a34a":"#dc2626")}}>
                            {pctD==="—"?"—":`${Number(pctD)>=0?"+":""}${pctD}%`}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  {(data.summaries||[]).length>0 && (
                    <tr style={{borderTop:"2px solid #e5e7eb",fontWeight:700,background:"#f9fafb"}}>
                      <td colSpan={3} style={{padding:"8px 10px",fontSize:12}}>TOTAL CA / Résultat</td>
                      {data.summaries.map((s,i)=>(
                        <td key={i} style={{padding:"8px 10px",textAlign:"right",fontSize:12,color:LINE_COLORS[i]}}>
                          CA {fmt(s.ca)} · RN {fmt(s.resultat)}
                        </td>
                      ))}
                      {data.scenarios.length>1 && <td colSpan={2}/>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      ) : (
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
          color:"var(--text-muted)",gap:10,padding:60}}>
          <div style={{fontSize:40}}>⚖️</div>
          <div style={{fontSize:15,fontWeight:600}}>Comparaison multi-scénarios</div>
          <div style={{fontSize:12,textAlign:"center",maxWidth:300}}>
            Sélectionnez 2 à 4 scénarios dans le panneau de gauche, puis cliquez sur «&nbsp;Comparer&nbsp;».
          </div>
          {selected.length>=2 && !loading && (
            <button onClick={compare} style={{marginTop:8,padding:"9px 20px",background:"#3b82f6",color:"#fff",
              border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              ⚖️ Lancer la comparaison
            </button>
          )}
        </div>
      )}
    </div>
  );
}
